import type { ImportDto } from '@performance-crm/shared';
import { useMemo, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { useAdvisors, useImport, useProjects, useRetryImport } from '@/lib/queries';

/**
 * Reproceso en bloque de las filas con error de una importación: muestra por qué fallaron y deja corregir el
 * proyecto / asesor por defecto o ignorar la columna "Asesor" antes de volver a procesar solo esas filas.
 */
export function ImportRetryDialog({
  imp,
  companyId,
  onClose,
}: {
  imp: ImportDto;
  companyId: string;
  onClose: () => void;
}) {
  const { data: detail } = useImport(imp.id);
  const { data: projects } = useProjects();
  const { data: advisors } = useAdvisors(companyId);
  const retry = useRetryImport();
  const toast = useToast();
  const [projectId, setProjectId] = useState(imp.projectId ?? '');
  const [advisorId, setAdvisorId] = useState(imp.advisorId ?? '');
  const [ignoreAdvisorColumn, setIgnoreAdvisorColumn] = useState(false);
  const companyProjects = useMemo(
    () => projects?.filter((p) => p.companyId === companyId) ?? [],
    [projects, companyId],
  );
  const humanAdvisors = useMemo(
    () => (advisors ?? []).filter((a) => a.kind === 'HUMAN'),
    [advisors],
  );
  const summary = detail?.errorSummary ?? [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await retry.mutateAsync({
        id: imp.id,
        projectId: projectId || null,
        advisorId: advisorId || null,
        ignoreAdvisorColumn,
      });
      toast.success(
        'Reproceso iniciado',
        `${r.queued} filas vuelven a procesarse; el resultado aparece en la tabla.`,
      );
      onClose();
    } catch (err) {
      toast.error('No se pudo reprocesar', (err as Error).message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Reprocesar filas con error</DialogTitle>
            <DialogDescription>
              {imp.name}: {imp.errors === 1 ? 'falló 1 fila' : `fallaron ${imp.errors} filas`} de{' '}
              {imp.totalRows}. Solo esas vuelven a procesarse; las que ya entraron no se tocan.
            </DialogDescription>
          </DialogHeader>
          {summary.length > 0 && (
            <div className="rounded-md border text-sm" data-testid="retry-summary">
              <p className="bg-muted/50 text-muted-foreground px-3 py-1.5 text-xs font-medium uppercase">
                Por qué fallaron
              </p>
              <ul className="divide-y">
                {summary.map((s) => (
                  <li key={s.error} className="flex justify-between gap-3 px-3 py-1.5">
                    <span className="text-danger">{s.error}</span>
                    <span className="text-muted-foreground shrink-0">{s.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="retry-project">Proyecto por defecto</Label>
              <Select
                id="retry-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                data-testid="retry-project"
              >
                <option value="">— (usar la columna "Proyecto" de cada fila)</option>
                {companyProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <p className="text-muted-foreground text-xs">
                Para las filas que fallaron por "Sin proyecto" o "Proyecto no encontrado".
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="retry-advisor">Asesor por defecto</Label>
              <Select
                id="retry-advisor"
                value={advisorId}
                onChange={(e) => setAdvisorId(e.target.value)}
                data-testid="retry-advisor"
              >
                <option value="">— (por turnos del proyecto)</option>
                {humanAdvisors.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.fullName}
                  </option>
                ))}
              </Select>
              <p className="text-muted-foreground text-xs">
                Recibe las filas que no traen asesor propio.
              </p>
            </div>
          </div>
          {imp.advisorColumn && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={ignoreAdvisorColumn}
                onChange={(e) => setIgnoreAdvisorColumn(e.target.checked)}
                data-testid="retry-ignore-advisor"
              />
              <span>
                Ignorar la columna "{imp.advisorColumn}" del archivo
                <span className="text-muted-foreground block text-xs">
                  Las filas con un asesor que no existe en el CRM pasan al asesor por defecto
                  elegido arriba (o al reparto automático del proyecto).
                </span>
              </span>
            </label>
          )}
          <p className="text-muted-foreground text-xs">
            Las filas con datos mal escritos o sin celular, correo ni DNI volverán a fallar aquí:
            corrígelas una por una desde Eventos → "Corregir fila".
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={retry.isPending} data-testid="retry-confirm">
              Reprocesar {imp.errors === 1 ? '1 fila' : `${imp.errors} filas`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
